import React, { useCallback, useState } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { CreateHomeworkActivityForm } from "../components/CreateHomeworkActivityForm";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { homeworkActivityDetailPath } from "../config/navigation";
import { useActivityTemplates } from "../hooks/useActivityTemplates";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

const homeworkActivitiesPath = "/clinician-portal/homework-activities";

export function AddHomeworkActivityPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const request = useCallback(
    async (signal: AbortSignal) => {
      const [clients, activityTypes] = await Promise.all([
        clinicianPortalApi.getMyClients(clinicianCode, signal),
        clinicianPortalApi.getActivityTypes(clinicianCode, signal),
      ]);
      return { clients, activityTypes };
    },
    [clinicianCode],
  );
  const state = usePortalData(request);
  const activityTemplates = useActivityTemplates(clinicianCode);
  const clientValuesRequest = useCallback(
    async (signal: AbortSignal) => ({
      patientId: selectedPatientId,
      response: selectedPatientId
        ? await clinicianPortalApi.getClientValues(
            clinicianCode,
            selectedPatientId,
            signal,
          )
        : null,
    }),
    [clinicianCode, selectedPatientId],
  );
  const clientValuesState = usePortalData(clientValuesRequest);
  const selectedTemplateRequest = useCallback(
    async (signal: AbortSignal) => ({
      templateId: selectedTemplateId,
      response: selectedTemplateId
        ? await clinicianPortalApi.getActivityTemplate(
            clinicianCode,
            selectedTemplateId,
            signal,
          )
        : null,
    }),
    [clinicianCode, selectedTemplateId],
  );
  const selectedTemplateState = usePortalData(selectedTemplateRequest);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.clients.therapist &&
      data.activityTypes.found &&
      data.activityTypes.authorized &&
      data.activityTypes.therapist,
  );
  const activatedClients =
    data?.clients.patients.filter((client) => client.status === "Activated") ??
    [];
  const clientValuesData =
    clientValuesState.status === "success" ||
    clientValuesState.status === "refreshing"
      ? clientValuesState.data
      : undefined;
  const currentClientValuesResponse =
    clientValuesData?.patientId === selectedPatientId
      ? clientValuesData.response
      : null;
  const clientValuesAvailable = Boolean(
    currentClientValuesResponse?.found &&
      currentClientValuesResponse.authorized &&
      currentClientValuesResponse.patient?.id === selectedPatientId,
  );
  const clientValuesLoading = Boolean(
    selectedPatientId &&
      !clientValuesAvailable &&
      clientValuesState.status !== "error" &&
      clientValuesData?.patientId !== selectedPatientId,
  );
  const clientValuesError = selectedPatientId
    ? clientValuesState.status === "error"
      ? clientValuesState.error
      : clientValuesData?.patientId === selectedPatientId &&
          !clientValuesAvailable
        ? new Error("Client values are unavailable")
        : undefined
    : undefined;
  const selectedTemplateData =
    selectedTemplateState.status === "success" ||
    selectedTemplateState.status === "refreshing"
      ? selectedTemplateState.data
      : undefined;
  const currentTemplateResponse =
    selectedTemplateData?.templateId === selectedTemplateId
      ? selectedTemplateData.response
      : null;
  const selectedTemplateAvailable = Boolean(
    currentTemplateResponse?.found &&
      currentTemplateResponse.authorized &&
      currentTemplateResponse.template?.id === selectedTemplateId &&
      currentTemplateResponse.template.status === "On",
  );
  const selectedTemplateLoading = Boolean(
    selectedTemplateId &&
      !selectedTemplateAvailable &&
      selectedTemplateState.status !== "error" &&
      selectedTemplateData?.templateId !== selectedTemplateId,
  );
  const selectedTemplateError = selectedTemplateId
    ? selectedTemplateState.status === "error"
      ? selectedTemplateState.error
      : selectedTemplateData?.templateId === selectedTemplateId &&
          !selectedTemplateAvailable
        ? new Error("Activity Template is unavailable")
        : undefined
    : undefined;
  return (
    <ClinicianPortalLayout
      activeGlobalKey="homework"
      clinicianCode={clinicianCode}
      therapistRole={data?.clients.therapist?.role}
    >
      <PortalHeader
        title="Add Homework Activity"
        clinicianName={
          available ? data?.clients.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Homework Activities", path: homeworkActivitiesPath },
              { label: "Add Homework Activity" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--docked-form">
        {state.status === "loading" ? (
          <LoadingState label="Loading Homework Activity form" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Homework Activity creation is unavailable")}
          />
        ) : null}
        {available && data && !activatedClients.length ? (
          <EmptyState
            title="No activated clients available"
            description="Activate a client before creating a Homework Activity."
          />
        ) : null}
        {available && data && activatedClients.length ? (
          <CreateHomeworkActivityForm
            clinicianCode={clinicianCode}
            clients={activatedClients}
            activityTypes={data.activityTypes.activityTypes}
            templates={activityTemplates.templates}
            templatesLoading={activityTemplates.isLoading}
            templatesError={activityTemplates.error}
            retryTemplates={activityTemplates.retry}
            selectedTemplate={
              selectedTemplateAvailable && currentTemplateResponse
                ? currentTemplateResponse.template
                : null
            }
            selectedTemplateLoading={selectedTemplateLoading}
            selectedTemplateError={selectedTemplateError}
            retrySelectedTemplate={selectedTemplateState.refresh}
            clientValues={
              clientValuesAvailable && currentClientValuesResponse
                ? currentClientValuesResponse.values
                : []
            }
            clientValuesLoading={clientValuesLoading}
            clientValuesError={clientValuesError}
            retryClientValues={clientValuesState.refresh}
            cancelPath={homeworkActivitiesPath}
            isSubmitting={mutation.state.status === "pending"}
            submissionError={
              mutation.state.status === "error"
                ? mutation.state.error
                : undefined
            }
            clearSubmissionError={mutation.reset}
            onPatientChange={setSelectedPatientId}
            onTemplateChange={setSelectedTemplateId}
            onSubmit={async (patientId, fields) => {
              const result = await mutation.run(
                "create-homework-activity",
                async (signal) => {
                  const response =
                    await clinicianPortalApi.createHomeworkActivity(
                      clinicianCode,
                      patientId,
                      fields,
                      signal,
                    );
                  if (
                    !response.success ||
                    !response.created ||
                    !response.authorized ||
                    !response.homework?.id
                  ) {
                    throw new ClinicianPortalApiError(
                      response.error ||
                        "The Homework Activity could not be created.",
                      response.authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
                      response.authorized,
                    );
                  }
                  return response;
                },
              );
              if (result?.homework?.id) {
                navigateWithinPortal(
                  withClinicianCode(
                    homeworkActivityDetailPath(patientId, result.homework.id),
                    clinicianCode,
                  ),
                );
              }
            }}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
